import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';
import { CasinoService, ChallengeDetails, IChallengeInstanceEGM, IChallengeItem } from 'services/casino';
import { buildCommonUrl } from 'pages/EventsTab/EventCard';

type SlotCommands = ReturnType<typeof SlotReduxCommandCreator>;
type EGMPinConfig = Record<string, { pinDisplayType: string; pinDisplayCode: string }> | null;

/**
 * Merges a challenge's presentation details with template defaults under
 * instance-level overrides (`challenge.details` wins over
 * `challenge.challenge.details`) — the same precedence used elsewhere for
 * challenge details. This matters for the Explore action because list endpoints
 * may only populate the template details, whereas the detail endpoint populates
 * the instance details; merging lets both resolve the same theme.
 */
const resolveChallengeDetails = (challenge?: IChallengeItem | null): ChallengeDetails =>
  ({ ...challenge?.challenge?.details, ...challenge?.details });

/**
 * Resolves the explore-header background image for a challenge from its
 * `details.exploreHeaderStlye`. Returns undefined when no explore header is
 * configured.
 */
export const getChallengeHeaderBgUrl = (challenge?: IChallengeItem | null): string | undefined => {
  const exploreHeaderStyle = resolveChallengeDetails(challenge).exploreHeaderStlye;
  if (!exploreHeaderStyle) return undefined;
  return buildCommonUrl('assets/images/headers', exploreHeaderStyle);
};

/**
 * Builds the per-EGM pin theme config (machineId -> pin display) from the
 * challenge's enrolled EGMs, keeping only machines that carry both a pin display
 * type and code. Mirrors the mapping the challenge detail page uses.
 */
const buildEgmPinConfig = (egms: IChallengeInstanceEGM[]): EGMPinConfig => {
  const config: Record<string, { pinDisplayType: string; pinDisplayCode: string }> = {};
  egms.forEach((egm) => {
    if (egm.machineId && egm.pinDisplayType && egm.pinDisplayCode) {
      config[String(egm.machineId)] = {
        pinDisplayType: egm.pinDisplayType,
        pinDisplayCode: egm.pinDisplayCode,
      };
    }
  });
  return Object.keys(config).length > 0 ? config : null;
};

/**
 * Applies the challenge's floor-map slot selection and per-EGM pin theme config.
 * The slot set is the config's EGMs when a theme config is available, otherwise
 * the challenge's `egms` CSV. Extracted so it can run twice: once synchronously
 * with whatever the caller provided, then again once the enrolled-EGM config has
 * been fetched (for the card Explore path that has no config up front).
 */
const applyChallengeSlots = (
  slotCommands: SlotCommands,
  challenge: IChallengeItem | null | undefined,
  egmPinConfig: EGMPinConfig
): void => {
  const slotsFromEGMs = egmPinConfig ? Object.keys(egmPinConfig) : null;
  // Keep only numeric slot numbers. A challenge's `egms` CSV can carry a
  // non-numeric sentinel (e.g. "all" for an all-EGMs challenge); left in, it
  // would flow into casinoParams.slotnumbers ("slotnumbers=all") and the report
  // endpoints reject it, surfacing an immediate "Failed to retrieve casino
  // report." toast on Explore even though the real EGM set loads a moment later
  // from GetChallengeInstanceEGMs. Dropping non-numeric tokens leaves an empty
  // set (full floor) until the real EGMs arrive.
  const slotsFromCsv = challenge?.egms
    ? challenge.egms.split(',').map((s) => s.trim()).filter((s) => /^\d+$/.test(s))
    : null;
  const slots = slotsFromEGMs ?? slotsFromCsv ?? [];

  if (slots.length > 0) {
    slotCommands.SetSearchMode(true);
    slotCommands.SelectBankFilter(false);
    slotCommands.SelectedSlotsName(challenge?.name || '');
    slotCommands.SelectedSlots(slots);
  } else {
    slotCommands.SetSearchMode(false);
    slotCommands.SelectedSlotsName('');
    slotCommands.SelectedSlots([]);
  }

  slotCommands.SetChallengeEGMPinConfig(egmPinConfig);
};

/**
 * Applies the "Explore" action for a challenge card: it filters the floor map to
 * the challenge's EGMs, seeds the challenge presentation state (header, name, pin
 * styling) and navigates to the map. Shared by every place a challenge card is
 * rendered so the Explore button behaves identically everywhere.
 *
 * `egmPinConfig` is the per-EGM pin theme config; the challenge detail page fetches
 * it (via GetChallengeInstanceEGMs) and passes it in. Card Explore buttons don't
 * have it, so when it's absent this fetches the enrolled EGMs itself (using
 * `casinoId` + the challenge's instance `uuid`) and re-applies the slot selection
 * and config, so the themed pins render on the map from any Explore entry point.
 */
export const openChallengeExplore = (
  slotCommands: SlotCommands,
  history: { push: (path: string) => void },
  challenge?: IChallengeItem | null,
  egmPinConfig: EGMPinConfig = null,
  casinoId?: number
): void => {
  const details = resolveChallengeDetails(challenge);

  applyChallengeSlots(slotCommands, challenge, egmPinConfig);
  slotCommands.SetChallengeHeaderBgUrl(getChallengeHeaderBgUrl(challenge) ?? null);
  slotCommands.SetChallengeName(challenge?.name ?? null);
  slotCommands.SetChallengePinDetails({
    showInsightTopPins: details.showInsightTopPins ?? null,
    pinBackgroundColor: details.pinBackgroundColor ?? null,
    pinNumberColor: details.pinNumberColor ?? null,
  });

  history.push('/tabs/home');

  if (!egmPinConfig && challenge?.uuid && casinoId) {
    new CasinoService()
      .GetChallengeInstanceEGMs(challenge.uuid, casinoId)
      .subscribe((egms) => {
        if (!Array.isArray(egms)) return;
        const fetchedConfig = buildEgmPinConfig(egms);
        if (fetchedConfig) {
          applyChallengeSlots(slotCommands, challenge, fetchedConfig);
        }
      });
  }
};
